import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../../services/api';

function UserForm({ user, onSave, onError, onCancel }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'editor',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'editor',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'editor',
        is_active: true
      });
    }
    setErrors({});
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      newErrors.username = 'Username must be 3-20 characters and contain only letters, numbers, and underscores';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only for new users or when password is provided)
    if (!isEditing) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters long';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData.password) {
      // When editing and password is provided
      if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters long';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isEditing) {
        // Update existing user
        const updateData = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active
        };

        await usersAPI.update(user.id, updateData);

        // Update password separately if provided
        if (formData.password) {
          await usersAPI.updatePassword(user.id, {
            new_password: formData.password
          });
        }

        onSave(`User "${formData.username}" has been updated successfully`);
      } else {
        // Create new user
        const createData = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          is_active: formData.is_active
        };

        await usersAPI.create(createData);
        onSave(`User "${formData.username}" has been created successfully`);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} user`;
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'editor',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'editor',
        is_active: true
      });
    }
    setErrors({});
  };

  return (
    <div className="user-form">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className={`fas ${isEditing ? 'fa-user-edit' : 'fa-user-plus'} me-2`}></i>
            {isEditing ? `Edit User: ${user.username}` : 'Add New User'}
          </h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Basic Information */}
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label required">Username</label>
                  <input
                    type="text"
                    className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Enter username"
                    disabled={loading}
                  />
                  {errors.username && (
                    <div className="invalid-feedback">{errors.username}</div>
                  )}
                  <small className="form-text text-muted">
                    3-20 characters, letters, numbers, and underscores only
                  </small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label required">Email</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    disabled={loading}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>

              {/* Password Section */}
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    Password {!isEditing && <span className="text-danger">*</span>}
                  </label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={isEditing ? 'Leave blank to keep current password' : 'Enter password'}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>
                  <small className="form-text text-muted">
                    Minimum 6 characters
                  </small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">
                    Confirm Password {(!isEditing || formData.password) && <span className="text-danger">*</span>}
                  </label>
                  <div className="input-group">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback">{errors.confirmPassword}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Role and Status */}
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="viewer">Viewer - Read-only access</option>
                    <option value="editor">Editor - Can manage content</option>
                    <option value="admin">Admin - Full system access</option>
                  </select>
                  <small className="form-text text-muted">
                    Select the appropriate access level
                  </small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-label">Status</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="is_active"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      {formData.is_active ? (
                        <>
                          <span className="badge bg-success me-2">Active</span>
                          User can login and access the system
                        </>
                      ) : (
                        <>
                          <span className="badge bg-warning me-2">Inactive</span>
                          User cannot login to the system
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'} me-2`}></i>
                    {isEditing ? 'Update User' : 'Create User'}
                  </>
                )}
              </button>
              
              <button
                type="button"
                className="btn btn-secondary ms-2"
                onClick={handleReset}
                disabled={loading}
              >
                <i className="fas fa-undo me-2"></i>
                Reset
              </button>
              
              <button
                type="button"
                className="btn btn-outline-secondary ms-2"
                onClick={onCancel}
                disabled={loading}
              >
                <i className="fas fa-times me-2"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .user-form .card {
          border: none;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          border-radius: 10px;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 2px solid #e9ecef;
          padding: 1.25rem 1.5rem;
        }

        .card-title {
          color: #495057;
          font-weight: 600;
        }

        .form-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .form-label.required::after {
          content: ' *';
          color: #dc3545;
        }

        .form-control,
        .form-select {
          border: 2px solid #e9ecef;
          border-radius: 6px;
          padding: 0.625rem 0.875rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #c9302c;
          box-shadow: 0 0 0 0.2rem rgba(201, 48, 44, 0.25);
        }

        .form-control.is-invalid {
          border-color: #dc3545;
        }

        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: 0.25rem;
          font-size: 0.875rem;
          color: #dc3545;
        }

        .form-text {
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        .input-group .btn {
          border: 2px solid #e9ecef;
          border-left: none;
        }

        .form-check-input {
          width: 2rem;
          height: 1.2rem;
          margin-right: 0.75rem;
        }

        .form-check-label {
          font-size: 0.95rem;
          line-height: 1.4;
        }

        .badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.35em 0.65em;
        }

        .form-actions {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 2px solid #e9ecef;
          display: flex;
          gap: 0.5rem;
        }

        .btn {
          font-weight: 500;
          padding: 0.625rem 1.25rem;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background-color: #c9302c;
          border-color: #c9302c;
        }

        .btn-primary:hover {
          background-color: #a02622;
          border-color: #a02622;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
        }

        .btn-outline-secondary {
          color: #6c757d;
          border-color: #6c757d;
        }

        .btn-outline-secondary:hover {
          background-color: #6c757d;
          border-color: #6c757d;
          color: white;
        }

        @media (max-width: 768px) {
          .form-actions {
            flex-direction: column;
          }
          
          .form-actions .btn {
            width: 100%;
            margin-bottom: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}

export default UserForm;